/**
 * AnimatedListItem
 *
 * Generic wrapper that animates a new list item into view:
 *   1. Grow vertically from 0 to auto height (content pushes siblings down)
 *   2. Fade the content in once fully expanded
 *
 * Usage:
 *   <AnimatePresence initial={false}>
 *     {items.map(item => (
 *       <AnimatedListItem key={item.id}>
 *         <MyCard item={item} />
 *       </AnimatedListItem>
 *     ))}
 *   </AnimatePresence>
 */

import { forwardRef, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { DURATION_SLOW, EASE_OUT } from '@/lib/animation';

interface AnimatedListItemProps {
  children: ReactNode;
  /** When true, skip the entrance animation and render in final state immediately. */
  skipEntrance?: boolean;
  /**
   * When true, opt out of the height grow/shrink animation and instead
   * stretch to fill the flex parent (`flex-1 min-h-0`). Used for
   * single-surface canvases where the surface needs to claim available
   * height instead of being content-sized.
   */
  fill?: boolean;
}

// forwardRef so framer-motion can measure us when used inside
// `<AnimatePresence mode="popLayout">` (it needs to position the exiting
// element absolutely before it animates out).
export const AnimatedListItem = forwardRef<HTMLDivElement, AnimatedListItemProps>(
  function AnimatedListItem({ children, skipEntrance, fill }, ref) {
  if (fill) {
    return (
      <motion.div
        ref={ref}
        className="flex flex-col flex-1 min-h-0"
        initial={skipEntrance ? false : { opacity: 0 }}
        animate={{
          opacity: 1,
          transition: skipEntrance
            ? { duration: 0 }
            : { opacity: { duration: DURATION_SLOW, ease: EASE_OUT } },
        }}
        exit={{
          opacity: 0,
          transition: { opacity: { duration: DURATION_SLOW, ease: EASE_OUT } },
        }}
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.div
      ref={ref}
      initial={skipEntrance ? false : { height: 0, opacity: 0, overflow: 'hidden' }}
      animate={{
        height: 'auto',
        opacity: 1,
        overflow: 'visible',
        transition: skipEntrance
          ? { duration: 0 }
          : {
              height: { duration: DURATION_SLOW, ease: EASE_OUT },
              opacity: { duration: DURATION_SLOW, ease: EASE_OUT, delay: DURATION_SLOW },
              overflow: { delay: DURATION_SLOW * 2 },
            },
      }}
      exit={{
        height: 0,
        opacity: 0,
        overflow: 'hidden',
        transition: {
          opacity: { duration: DURATION_SLOW, ease: EASE_OUT },
          height: { duration: DURATION_SLOW, ease: EASE_OUT, delay: DURATION_SLOW },
        },
      }}
    >
      {children}
    </motion.div>
  );
  },
);
